import React, { useState } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';

import Layout from '../components/layout';
import Helmet from '../components/basic/Helmet';
import Text from '../components/basic/Text';
import Link from '../components/util/link';
import Hero from '../components/basic/Hero';
import Section from '../components/basic/layouts/Section';
import { Grid, Cell } from '../components/basic/layouts/Grid';
import HTMLWrapper from '../components/basic/HTMLWrapper';
import getTextInElement from '../components/basic/helper-functions/getTextInElement.js';

import AccountManagement from '../images/contact/AccountManagement.svg';
import Jobs from '../images/contact/Jobs.svg';
import PressBrand from '../images/contact/PressBrand.svg';
import Sales from '../images/contact/Sales.svg';

import theme from '../styles/theme';
import documentToReactComponents from '../components/util/documentToReactComponents';

const {
	colors,
	breakPoints: { screenQuery },
} = theme;

const Wrapper = styled(Section)`
	padding: 20px;
	width: 100%;
	white-space: pre-wrap;
	${screenQuery.large} {
		display: none;
	}
`;

const MobileWrapper = styled(Section)`
	display: none;
	white-space: pre-wrap;
	@media only screen and (max-width: ${theme.breakPoints.large}) {
		display: block;
		padding: 10px;
		width: 100%;
	}
`;
const ScreenshotText = styled.div`
	position: relative;
	:hover {
		cursor: pointer;
	}
	width: 100%;
	* {
		width: 100%;
		height: 100%;
	}

	margin-bottom: 30px;
	&:last-child {
		margin-bottom: 0px;
	}

	.screenshot-element {
		padding-right: 60px;
	}

	.screenshot-header {
		padding-top: 2px;
		padding-left: 0px;
		transition-duration: ${theme.transitionDuration};
		border-left: 0px solid transparent;
		${props =>
			props.active
				? `
	  padding-right: 43px;
	  padding-left: 10px;
	  border-left: 7px solid ${colors.brandMain};
	`
				: ``}
	}

	@media only screen and (max-width: ${theme.breakPoints.large}) {
		:hover {
			cursor: inherit;
		}
	}
`;

const SideTabDesktopCell = styled(Cell)`
	align-self: flex-start;
	margin: 0;
`;

const Screenshot = styled.div`
	grid-area: screenshot;

	iframe {
		height: 70vh;
	}
	img {
		width: 100%;
		position: relative;
	}
	@media only screen and (max-width: ${theme.breakPoints.large}) {
		width: 69%;
		margin: 60px auto;
	}
`;

const ImageWrapperSide = styled.div`
	background-color: ${colors.white};
	box-shadow: ${theme.hoverBoxShadow};
	${screenQuery.large} {
		box-shadow: ${theme.boxShadow};
	}
	padding: 20px;
`;

const MobileHeader = styled.div`
	margin: 30px 0 10px;

	p {
		margin: 0;
		padding: 0;
	}
`;

const SideTabList = ({ tabData }) => {
	const [screenIndex, setScreenIndex] = useState(0);

	return (
		<>
			<Wrapper key="screenshots-desktop">
				<Grid>
					<SideTabDesktopCell align="center" span={4}>
						{tabData.map((tab, index) => {
							const active = index === screenIndex;
							return (
								<ScreenshotText
									onMouseOver={() => setScreenIndex(index)}
									onClick={() => setScreenIndex(index)}
									active={active}
									index={index}
								>
									<Text
										component="h5"
										className="screenshot-header screenshot-element"
									>
										{tab.title}
									</Text>
									<HTMLWrapper html={tab.address} />
								</ScreenshotText>
							);
						})}
					</SideTabDesktopCell>
					<Cell align="center" span={8}>
						<Screenshot>
							<ImageWrapperSide>
								{screenIndex !== 3 ? (
									<iframe
										src={tabData[screenIndex]?.googleMapLocation}
										width="100%"
										height="auto"
										style={{ border: 0 }}
										allowfullscreen=""
										loading="lazy"
										referrerpolicy="no-referrer-when-downgrade"
									/>
								) : (
									// this offices breaks in google if not embedded directly for some reason
									<iframe
										src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.511307815712!2d75.89087702530558!3d22.746398529368324!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fd56993c50d5%3A0xbdcc27d0aa89f45b!2sPrinces&#39;%20Business%20Skypark!5e0!3m2!1sen!2sus!4v1741890383058!5m2!1sen!2sus"
										width="100%"
										height="auto"
										style={{ border: 0 }}
										allowfullscreen=""
										loading="lazy"
										referrerpolicy="no-referrer-when-downgrade"
									></iframe>
								)}
							</ImageWrapperSide>
						</Screenshot>
					</Cell>
				</Grid>
			</Wrapper>
			<MobileWrapper key="screenshots-mobile">
				{tabData.map((tab, index) => {
					return (
						<>
							<MobileHeader>
								<Text component="h5" color="black">
									{tab.title}
								</Text>
								<HTMLWrapper html={tab.address} />
							</MobileHeader>
							{index === 3 ? (
								// this offices breaks in google if not embedded directly for some reason
								<iframe
									src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.511307815712!2d75.89087702530558!3d22.746398529368324!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fd56993c50d5%3A0xbdcc27d0aa89f45b!2sPrinces&#39;%20Business%20Skypark!5e0!3m2!1sen!2sus!4v1741890383058!5m2!1sen!2sus"
									width="100%"
									height="auto"
									style={{ border: 0 }}
									allowfullscreen=""
									loading="lazy"
									referrerpolicy="no-referrer-when-downgrade"
								></iframe>
							) : (
								<iframe
									src={tab.googleMapLocation}
									width="100%"
									height="200"
									style={{ border: 0 }}
									allowfullscreen=""
									loading="lazy"
									referrerpolicy="no-referrer-when-downgrade"
									title={tab.googleMapTitle}
								/>
							)}
						</>
					);
				})}
			</MobileWrapper>
		</>
	);
};

const navigationCards = [
	{
		image: Sales,
		header: 'Contact sales',
		subheader:
			'Evaluating our platform and need advice before you buy? Get in touch with our sales team.',
		linkText: `CONTACT SALES${` →`}`,
		linkLocation: '/contact-sales/',
	},
	{
		image: AccountManagement,
		header: 'Contact customer success',
		subheader:
			'Already a customer? Contact customer success for billing and upgrade questions.',
		linkText: 'CONTACT US →',
		linkLocation: '/contact-customer-success/',
	},
	{
		image: PressBrand,
		header: 'Press and brand resources',
		subheader: 'Featured news highlights, brand resources, and press requests.',
		linkText: `VISIT PRESS PAGE${` →`}`,
		linkLocation: '/press-and-brand-resources/',
	},
	{
		image: Jobs,
		header: 'Careers',
		subheader: 'Join the Submittable team.',
		linkText: `BROWSE CAREERS${` →`}`,
		linkLocation: '/careers/',
	},
];

const SupportCard = styled(Link)`
	padding: 35px 25px 20px;
	text-align: center;
	margin: 40px auto 0;
	max-width: 450px;
	box-shadow: ${theme.boxShadow};
	text-wrap: balance;
	h4 {
		font-size: 20px;
	}
	display: flex;
	flex-direction: column;

	p {
		margin-top: 12px;
	}

	:hover {
		p {
			color: ${colors.black};
		}

		.link-text {
			color: ${colors.brandMain};
		}
	}
`;
const NavigationCard = styled(Cell)`
	text-align: center;
`;
const ImageWrapper = styled.div`
	height: 90px;
	width: 90px;
	margin: 0 auto 30px;
	display: flex;
	align-items: center;
	justify-content: center;
	img {
		width: 100%;
	}
`;

const ContactGrid = styled(Grid)`
	white-space: pre-wrap;
	grid-columng-gap: 60;
	grid-row-gap: 20;
`;

const ContactCell = styled.div`
	background-color: ${colors.grayscale.lighter};
	grid-column: -9 / -1;
	text-align: center;
	padding: 40px;

	${screenQuery.medium} {
		grid-column: 1 / -1;
	}
`;

const Page = ({ data, location }) => {
	const {
		updatedAt,
		metaTitle,
		metaDescription,
		heroHeader,
		heroSubheaderLong,
		sections,
	} = data.contentfulPageTemplateCustom;

	const heroSubheader = getTextInElement(
		heroSubheaderLong.childMarkdownRemark.html,
		'p'
	);

	const officeLocations = [
		{
			title: 'Montana Office',
			address: '<p>101 E Front Street,<br />Ste 500, Missoula, MT 59802<p>',
			googleMapLocation:
				'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2727.6679445834766!2d-113.99731168413834!3d46.86990667914278!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x535dcc2b3d0b16d7%3A0x4ceced47b07ec47f!2sSubmittable!5e0!3m2!1sen!2sus!4v1670027524651!5m2!1sen!2sus',
		},
		{
			title: 'Washington Office',
			address: '<p>777 108th Ave NE, Suite 2020<br />Bellevue, WA 98004</p>',
			googleMapLocation:
				'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2689.5734624806773!2d-122.19806068411624!3d47.61498297918516!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54906d24f7dfbeb9%3A0x9063abef0612c154!2sSubmittable!5e0!3m2!1sen!2sus!4v1670027553940!5m2!1sen!2sus',
		},
		{
			title: 'Pennsylvania Office',
			address:
				'<p>555 East North Lane, Suite 5030<br/>Conshohocken, PA 19428</p>',
			googleMapLocation:
				'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d671.3783130052972!2d-75.28762048405548!3d40.07683167406835!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6be5ee3dff655%3A0xaf8d3f38d2a8a955!2sWizeHive%2C%20Inc.!5e0!3m2!1sen!2sus!4v1741890061821!5m2!1sen!2sus',
		},
		{
			title: 'India Office',
			address:
				'<p>602-603 Princes’ Business Skypark, Opposite Orbit Mall, Vijay Nagar<br/>Indore, Madhya Pradesh 452010</p>',
			googleMapLocation:
				'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3679.511307815712!2d75.89087702530558!3d22.746398529368324!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3962fd56993c50d5%3A0xbdcc27d0aa89f45b!2sPrinces&#39;%20Business%20Skypark!5e0!3m2!1sen!2sus!4v1741890383058!5m2!1sen!2sus',
		},
	];

	const mailingAddressData = sections.filter(
		data => data.contentful_id === '1E40fTwAzr4gme47Awys2c'
	)[0];

	const technicalSupportCard = sections.filter(
		data => data.contentful_id === '5oeHdHKUCr5MDZ7z2gwiHw'
	)[0];

	return (
		<Layout key="homepage" location={location}>
			<Helmet
				pageTitle={metaTitle}
				description={metaDescription}
				location={location}
				updatedAt={updatedAt}
			/>
			<Hero header={heroHeader} subheader={heroSubheader} noBottomPadding />
			<Section noTopPadding showOverflow>
				<SupportCard to={technicalSupportCard.link}>
					<ImageWrapper>
						<img src={technicalSupportCard.logo.file.url} />
					</ImageWrapper>
					<Text
						component="h4"
						style={{ margin: 0, fontFamily: 'ProximaNova-Regular' }}
					>
						{technicalSupportCard.header}
					</Text>
					<HTMLWrapper
						html={technicalSupportCard.bodyText.childMarkdownRemark.html}
						replaceNode={{ h2: 'h4' }}
					/>
					<Text
						component="caps"
						style={{ marginBottom: 15 }}
						className="link-text"
					>
						{technicalSupportCard.linkText}
					</Text>
				</SupportCard>
				<Grid className="margin-top">
					{navigationCards.map(card => {
						return (
							<NavigationCard span={3} spanMd={3} inlineMd spanSm={6} inlineSm>
								<ImageWrapper>
									<img src={card.image} />
								</ImageWrapper>
								<Text component="h5">{card.header}</Text>
								<Text component="b5" style={{ marginBottom: 30 }}>
									{card.subheader}
								</Text>
								<Text component="caps">
									<Link to={card.linkLocation} hovercolor="brandMain">
										{card.linkText}
									</Link>
								</Text>
							</NavigationCard>
						);
					})}
				</Grid>
			</Section>
			<Section backgroundColor={colors.grayscale.stone}>
				<Text component="h2" textAlign="center">
					Visit our offices
				</Text>
				<ContactGrid className="margin-top">
					<Cell>
						<SideTabList tabData={officeLocations} />
					</Cell>
					<ContactCell span={8}>
						<Text component="h5">{mailingAddressData.title}</Text>
						<HTMLWrapper>
							{documentToReactComponents(mailingAddressData.richText)}
						</HTMLWrapper>
					</ContactCell>
				</ContactGrid>
			</Section>
		</Layout>
	);
};

export default Page;

export const contactPageQuery = graphql`
	query contactPageQuery {
		contentfulPageTemplateCustom(
			contentful_id: { eq: "6v9OVQ1ZWUpqVrZernDgqz" }
		) {
			updatedAt
			...CustomHeroFragment
			sections {
				... on ContentfulCard {
					type: __typename
					contentful_id
					header
					bodyText {
						childMarkdownRemark {
							html
						}
					}
					link
					linkText
					logo {
						file {
							url
						}
					}
				}
				... on ContentfulBasicRichText {
					contentful_id
					title
					__typename
					richText {
						raw
					}
				}
				... on ContentfulSectionContentList {
					contentful_id
					sectionTitleIfApplicable
					cardSection {
						... on ContentfulBasicRichText {
							contentful_id
							title
							__typename
							richText {
								raw
							}
						}
					}
				}
			}
		}
	}
`;
